import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../store';
import {
  checkPublishedShiftPlanV2Enabled,
  setPublishedShiftPlanV2Enabled,
} from '../store/slices/v2/publishedShiftPlanFeatureSlice';
import { isLocalEnvironment } from '../utils/environmentDetection';

/**
 * Resolves the RCC_PUBLISHED_SHIFT_PLAN_V2 flag (shared via Redux so the published page and the
 * review/publish flow agree) for the given station. Local dev short-circuits to `true`.
 *
 * The flag always resolves — if the station hasn't loaded yet it resolves station-less (so the
 * page can never hang on its loader), then re-resolves once a real station arrives so site-level
 * gating (AllowedSites) evaluates against the real site. A ref tracks our station-less resolution
 * so the upgrade happens exactly once and a flag already resolved with a station is never
 * re-queried. Returns the current flag (`null` only until the first resolve).
 *
 * The station is passed in rather than read here, because callers source it differently
 * (CurrentPlanSummaryGated from the store, reviewAndPublish from a prop).
 */
export const useResolvePublishedShiftPlanV2 = (
  stationCode: string | null | undefined,
): boolean | null => {
  const dispatch = useAppDispatch();
  const flag = useAppSelector(
    (state) => state.publishedShiftPlanFeature?.isPublishedShiftPlanV2Enabled ?? null,
  );
  // True once we've resolved the flag without a station, so we upgrade to the real site exactly
  // once it arrives — without re-querying a flag that was already resolved with a station.
  const resolvedWithoutStationRef = useRef(false);

  useEffect(() => {
    // Local dev: getUserName() can't resolve without a Boson parent frame, so default to V2.
    if (isLocalEnvironment()) {
      if (flag === null) {
        dispatch(setPublishedShiftPlanV2Enabled(true));
      }
      return;
    }

    if (stationCode) {
      // First resolve, or upgrade a prior station-less resolution to the real site.
      if (flag === null || resolvedWithoutStationRef.current) {
        resolvedWithoutStationRef.current = false;
        dispatch(checkPublishedShiftPlanV2Enabled(stationCode));
      }
      return;
    }

    // No station yet: resolve station-less so the page can't hang on its loader; we re-resolve
    // above once a real station arrives.
    if (flag === null) {
      resolvedWithoutStationRef.current = true;
      dispatch(checkPublishedShiftPlanV2Enabled(''));
    }
  }, [dispatch, flag, stationCode]);

  return flag;
};
